// src/components/Header.jsx
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { CATEGORY_LINKS, SITE, createWhatsAppLink } from "../config/site";
import { InstagramIcon, TikTokIcon } from "./SocialIcons";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { isAuthed, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => {
    logout();
    setOpen(false);
    nav("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-orange-100 bg-white/95 backdrop-blur">
      <div className="bg-slate-950 text-white">
        <div className="mx-auto flex min-h-11 max-w-7xl items-center justify-between gap-3 px-3 py-2 sm:px-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm">
            <a href={SITE.phoneHref} className="font-semibold text-white hover:text-orange-200">
              {SITE.phoneDisplay}
            </a>
            <span className="hidden text-slate-300 md:inline">{SITE.fullLocation}</span>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-white/15 bg-white/5 p-2.5 text-white/90 transition hover:border-orange-200 hover:text-orange-200"
              aria-label="Instagram"
              title="Instagram"
            >
              <InstagramIcon className="h-5 w-5" />
            </a>
            <a
              href={SITE.tiktok}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-white/15 bg-white/5 p-2.5 text-white/90 transition hover:border-orange-200 hover:text-orange-200"
              aria-label="TikTok"
              title="TikTok"
            >
              <TikTokIcon className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-3 py-4 sm:px-4">
        <Link to="/" className="flex min-w-0 items-center gap-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-slate-900 bg-[#171717] shadow-lg sm:h-20 sm:w-20">
            <img
              src="/icon.png"
              alt="Petrah Collection logo"
              className="h-12 w-12 object-contain sm:h-16 sm:w-16"
            />
          </div>
          <div className="min-w-0">
            <div className="truncate text-lg font-black tracking-tight text-slate-950 sm:text-2xl">
              <span className="text-[#e46a2e]">petrah</span>
              <span>collection</span>
            </div>
            <p className="hidden text-sm text-slate-600 sm:block">{SITE.fullLocation}</p>
          </div>
        </Link>

        <div className="hidden items-center gap-2 lg:flex">
          <a
            href={SITE.phoneHref}
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#e46a2e] hover:text-[#e46a2e]"
          >
            Call us
          </a>
          <a
            href={createWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-[#e46a2e] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#cf5d27]"
          >
            WhatsApp order
          </a>
          <Link
            to="/cart"
            className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#e46a2e] hover:text-[#e46a2e]"
          >
            View cart
          </Link>
          {isAuthed ? (
            <button
              onClick={handleLogout}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
              title="Logout"
            >
              Logout
            </button>
          ) : (
            <Link
              to="/login"
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
              title="Login"
            >
              Login
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <Link
            to="/cart"
            className="rounded-full border border-slate-200 p-2 text-slate-700"
            title="Cart"
            aria-label="Cart"
          >
            <CartIcon />
          </Link>
          <button
            className="rounded-full border border-slate-200 p-2 text-slate-700"
            onClick={() => setOpen(true)}
            aria-label="Menu"
            title="Menu"
          >
            <MenuIcon />
          </button>
        </div>
      </div>

      <nav className="hidden border-t border-orange-100 bg-white lg:block">
        <div className="mx-auto flex min-h-14 max-w-7xl items-center justify-between gap-2 px-4">
          <div className="flex flex-wrap items-center gap-1">
            {CATEGORY_LINKS.map(([label, to]) => (
              <Link
                key={label}
                to={to}
                className="rounded-full px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-orange-50 hover:text-[#e46a2e]"
              >
                {label}
              </Link>
            ))}
          </div>

          <a
            href={createWhatsAppLink()}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-[#e46a2e]"
          >
            Order now
          </a>
        </div>
      </nav>

      <div className="border-t border-orange-100 bg-[linear-gradient(180deg,#fffaf7,#fff)]">
        <div className="mx-auto grid max-w-7xl gap-2 px-3 py-3 sm:grid-cols-2 lg:grid-cols-4 lg:px-4">
          <SummaryCard title="Location" value={`${SITE.locationTitle}\n${SITE.locationLine}`} />
          <SummaryCard title="Phone" value={SITE.phoneDisplay} href={SITE.phoneHref} />
          <SummaryCard title="Email" value={SITE.email} href={`mailto:${SITE.email}`} />
          <SummaryCard title="Hours" value={SITE.businessHoursDetailed} />
        </div>
      </div>

      <div className={`fixed inset-0 z-50 lg:hidden ${open ? "" : "pointer-events-none"}`}>
        <div
          className={`absolute inset-0 bg-slate-950/50 transition ${open ? "opacity-100" : "opacity-0"}`}
          onClick={() => setOpen(false)}
        />
        <aside
          className={`absolute right-0 top-0 h-full w-[88%] max-w-sm overflow-y-auto bg-white shadow-2xl transition-transform ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="border-b border-orange-100 px-5 py-5">
            <div className="flex items-start justify-between gap-3">
              <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3">
                <div className="flex h-16 w-16 items-center justify-center rounded-full border border-slate-900 bg-[#171717] shadow-lg">
                  <img src="/icon.png" alt="Petrah Collection logo" className="h-12 w-12 object-contain" />
                </div>
                <div>
                  <div className="text-lg font-black tracking-tight text-slate-950">
                    <span className="text-[#e46a2e]">petrah</span>
                    <span>collection</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{SITE.locationTitle}</p>
                </div>
              </Link>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full border border-slate-200 p-2 text-slate-700"
                aria-label="Close menu"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <a href={SITE.phoneHref} className="block font-semibold text-slate-900">
                {SITE.phoneDisplay}
              </a>
              <p>{SITE.fullLocation}</p>
              <a href={`mailto:${SITE.email}`} className="block break-all">
                {SITE.email}
              </a>
            </div>

            <div className="mt-4 flex gap-2">
              <a
                href={createWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-[#e46a2e] px-4 py-2 text-sm font-semibold text-white"
              >
                WhatsApp
              </a>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
              >
                Instagram
              </a>
            </div>
          </div>

          <ul className="space-y-1 px-3 py-4">
            {CATEGORY_LINKS.map(([label, to]) => (
              <li key={label}>
                <Link
                  to={to}
                  className="block rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-orange-50 hover:text-[#e46a2e]"
                  onClick={() => setOpen(false)}
                >
                  {label}
                </Link>
              </li>
            ))}

            <li className="mt-3 border-t border-slate-200 pt-3">
              {isAuthed ? (
                <button
                  onClick={handleLogout}
                  className="block w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-orange-50 hover:text-[#e46a2e]"
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  className="block rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-orange-50 hover:text-[#e46a2e]"
                  onClick={() => setOpen(false)}
                >
                  Login
                </Link>
              )}
            </li>
          </ul>
        </aside>
      </div>
    </header>
  );
}

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
      <path strokeWidth="1.7" d="M3 4h2l2.1 10.1a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.7L20 7H7" />
      <circle cx="10" cy="19" r="1.7" fill="currentColor" stroke="none" />
      <circle cx="17" cy="19" r="1.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
      <path strokeWidth="1.8" strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5">
      <path strokeWidth="1.8" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SummaryCard({ title, value, href }) {
  const content = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{title}</div>
      <div className="mt-1 whitespace-pre-line text-sm font-semibold leading-6 text-slate-900">{value}</div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className="rounded-[1.25rem] border border-slate-200 bg-white/90 px-4 py-3 shadow-sm transition hover:border-[#e46a2e]"
      >
        {content}
      </a>
    );
  }

  return <div className="rounded-[1.25rem] border border-slate-200 bg-white/90 px-4 py-3 shadow-sm">{content}</div>;
}
